import React from 'react';
import { classNames } from '../../utils/helpers';

// ============ Loading Spinner ============
export const Spinner = ({ size = 'md', className = '' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return (
    <svg className={classNames('animate-spin text-brand-600', sizes[size], className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
};

// ============ Page Loader ============
export const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Spinner size="lg" />
  </div>
);

// ============ Skeleton Card ============
export const SkeletonCard = ({ lines = 3 }) => (
  <div className="card animate-pulse">
    <div className="h-4 bg-slate-200 rounded w-1/3 mb-4" />
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-3 bg-slate-200 rounded mb-2" style={{ width: `${85 - i * 15}%` }} />
    ))}
  </div>
);

// ============ Empty State ============
export const EmptyState = ({ icon, title, description, action }) => (
  <div className="text-center py-12 px-4">
    {icon && <div className="text-slate-300 mb-4 flex justify-center">{icon}</div>}
    <h3 className="text-lg font-semibold text-slate-700 mb-1">{title}</h3>
    {description && <p className="text-sm text-slate-500 mb-4 max-w-sm mx-auto">{description}</p>}
    {action && action}
  </div>
);

// ============ Stat Card ============
export const StatCard = ({ label, value, change, icon, color = 'brand' }) => {
  const colorMap = {
    brand: 'bg-brand-50 text-brand-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-slate-900 font-heading">{value}</p>
          {change !== undefined && (
            <p className={classNames(
              'text-xs font-medium mt-1',
              change >= 0 ? 'text-emerald-600' : 'text-red-600'
            )}>
              {change >= 0 ? '+' : ''}{change}% from last month
            </p>
          )}
        </div>
        {icon && (
          <div className={classNames('w-10 h-10 rounded-xl flex items-center justify-center', colorMap[color])}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Avatar ============
export const Avatar = ({ name, size = 'md', src }) => {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-lg', xl: 'w-20 h-20 text-2xl' };
  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';

  if (src) {
    return <img src={src} alt={name} className={classNames('rounded-full object-cover', sizes[size])} />;
  }

  return (
    <div className={classNames(
      'rounded-full bg-brand-100 text-brand-700 font-semibold flex items-center justify-center flex-shrink-0',
      sizes[size]
    )}>
      {initials}
    </div>
  );
};

// ============ Modal ============
export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-lg' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={classNames('relative bg-white rounded-2xl shadow-elevated w-full p-6 animate-fade-in', maxWidth)}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900 font-heading">{title}</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// ============ Table ============
export const Table = ({ columns, data, onRowClick }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-100">
          {columns.map(col => (
            <th key={col.key} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr
            key={row.id || i}
            className={classNames('border-b border-slate-50 last:border-0', onRowClick && 'cursor-pointer hover:bg-slate-50')}
            onClick={() => onRowClick && onRowClick(row)}
          >
            {columns.map(col => (
              <td key={col.key} className="py-3 px-4 text-sm text-slate-700">
                {col.render ? col.render(row[col.key], row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ============ Badge ============
export const Badge = ({ children, variant = 'info' }) => {
  const variants = {
    info: 'bg-brand-50 text-brand-700',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-700',
    neutral: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={classNames('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', variants[variant])}>
      {children}
    </span>
  );
};

// ============ Tabs ============
export const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
    {tabs.map(tab => (
      <button
        key={tab.value}
        onClick={() => onChange(tab.value)}
        className={classNames(
          'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
          activeTab === tab.value
            ? 'bg-white text-brand-700 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        )}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// ============ Search Input ============
export const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="relative">
    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="input-field pl-10"
    />
  </div>
);

// ============ Progress Bar ============
export const ProgressBar = ({ value, max = 100, color = 'brand', showLabel = true }) => {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const colors = {
    brand: 'bg-brand-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
  };
  return (
    <div className="w-full">
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div className={classNames('h-2 rounded-full transition-all duration-500', colors[color])} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && <p className="text-xs text-slate-500 mt-1">{pct}%</p>}
    </div>
  );
};
